import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';

import { ModalService } from '@purespectrum1/ui/modal';

import {
  ActivityInterface,
  ActivityLogParamsInterface,
} from '../interfaces/manage-activity-logs.interface';
import { ActivityLogFilterHandler } from './domain/activity-log-filter-handler';
import { AuthService } from '../../shared/services/auth/auth.service';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { BuyerSurveyResponse } from '../../shared/interfaces/survey.interface';
import { Constants } from '../constants/manage-activity-logs.constants';
import { FilterDateRange } from '../../shared/interfaces/date';
import { ManageActivityLogsService } from '../services/manage-activity-logs/manage-activity-logs.service';
import {
  FilterOption,
  mapToFilterOptions,
} from './operators/map-to-filter-options.operator';
import { Milliseconds } from '../../shared/utils/milliseconds';
import { NotificationsModalComponent } from './components/notifications-modal/notifications-modal.component';
import { ProjectManagers } from '../../shared/interfaces/user.interface';
import { sort } from './operators/sort.operator';
import { SURVEY_STATUS_VALUES } from '../../shared/constants/survey-status';
import { SurveyStatus } from '../../shared/utils/survey-status';
import { SystemSettingsService } from '../../shared/services/system-settings/system-settings.service';
import { UserService } from '../../shared/services/user-service/user.service';

interface FormInterface {
  category: FilterOption<string>[];
  endDate: string;
  feature: FilterOption<string>[];
  message: string;
  startDate: string;
  status: FilterOption<number>[];
  user: FilterOption<string>[];
  userType: FilterOption<string>[];
}

@Component({
  selector: 'ps-ui-manage-activity-logs',
  templateUrl: './manage-activity-logs.component.html',
  styleUrls: ['./manage-activity-logs.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageActivityLogsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  public surveyId: number = 0;

  @Input()
  public notifications: boolean = false;

  @Input()
  public searchable: boolean = true;

  public datePickerOptions = {
    locale: { format: 'YYYY-MM-DD' },
    alwaysShowCalendars: false,
    showDropdowns: true,
  };

  public status: FilterOption<number>[] = [];
  public userTypes: FilterOption<string>[] = [
    { name: 'API Activity', value: Constants.USER_TYPES.API },
    {
      name: 'Purespectrum Platform Activity',
      value: Constants.USER_TYPES.PLATFORM,
    },
    { name: 'Project Manager Activity', value: Constants.USER_TYPES.USER },
  ];
  public users$: Observable<FilterOption<string>[]>;
  public categories$: Observable<FilterOption<string>[]>;
  public features$: Observable<FilterOption<string>[]>;
  public dates$: Observable<{ start: string; end: string }>;
  public logs$: Observable<ActivityInterface[]>;
  public emptyTableMessage$: Observable<string>;
  public survey$: Observable<BuyerSurveyResponse>;

  public form: FormGroup;

  private _fetchActivityLogs$ = new BehaviorSubject<number>(this.surveyId);

  private _subscriptions = new Subscription();

  constructor(
    private _auth: AuthService,
    private _buyerApiService: BuyerApiService,
    private _manageActivityLogsService: ManageActivityLogsService,
    private _systemSettingsService: SystemSettingsService,
    private _modal: ModalService,
    private _userService: UserService,
    private _datePipe: DatePipe,
    private fb: FormBuilder
  ) {}

  public ngOnInit(): void {
    this.status = SURVEY_STATUS_VALUES.map((status: number) => ({
      name: SurveyStatus[status],
      value: status,
    }));

    this.survey$ = this._buyerApiService
      .getSurvey(this.surveyId)
      .pipe(shareReplay(1));

    const initialLogs$ = this._fetchActivityLogs$.pipe(
      switchMap(() =>
        this._manageActivityLogsService.getActivityLogsData(this.surveyId)
      ),
      catchError(() => of({ activities: [] })),
      map(({ activities }) => activities),
      shareReplay(1)
    );

    const usersFromLogs$ = initialLogs$.pipe(
      map((logs) => this._extractUsers(logs))
    );

    this.users$ = combineLatest([
      usersFromLogs$,
      this._userService.getAllBuyerUser(this._auth.user?.cmp || 0),
    ]).pipe(
      map(([usersFromLogs, users]) => [...usersFromLogs, ...users]),
      mapToFilterOptions<Partial<ProjectManagers>, string>({
        keyName: 'name',
        keyValue: 'email',
      }),
      sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    );

    this.categories$ = initialLogs$.pipe(
      mapToFilterOptions<ActivityInterface, string>({
        keyName: 'category',
        keyValue: 'category',
      }),
      sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    );

    this.features$ = initialLogs$.pipe(
      mapToFilterOptions<ActivityInterface, string>({
        keyName: 'feature',
        keyValue: 'feature',
      }),
      sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    );

    this.form = this.fb.group({
      startDate: this.fb.control('', []),
      endDate: this.fb.control('', []),
      status: this.fb.control([], []),
      category: this.fb.control([], []),
      feature: this.fb.control([], []),
      user: this.fb.control([], []),
      userType: this.fb.control([], []),
      message: this.fb.control('', []),
    });

    this.dates$ = combineLatest([
      this.form.controls.startDate.valueChanges,
      this.form.controls.endDate.valueChanges,
    ]).pipe(
      map(([start, end]) => ({ start, end })),
      startWith({ start: '', end: '' })
    );

    const formChanges$ = this.form.valueChanges.pipe(
      debounceTime(500),
      startWith({}),
      map((value) => this.formFormatter(value)),
      shareReplay(1)
    );

    this.logs$ = combineLatest([initialLogs$, formChanges$]).pipe(
      map(([logs, params]) => new ActivityLogFilterHandler(params).filter(logs))
    );

    const filtering$ = formChanges$.pipe(
      map((params) => Object.values(params).length > 0)
    );

    this.emptyTableMessage$ = combineLatest([
      this._systemSettingsService.getSettings(),
      this.survey$,
      filtering$,
    ]).pipe(
      map(([{ activity_log }, survey, filtering]) =>
        this._emptyTableMessage(
          activity_log.default_expiration_time,
          survey.creation_date,
          filtering
        )
      )
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    this.surveyId = changes.surveyId.currentValue;
    if (!changes.surveyId.isFirstChange()) {
      this._fetchActivityLogs$.next(this.surveyId);
    }
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public onFilterDate(value: FilterDateRange) {
    const start = new Date(value.picker.startDate._d);
    const end = new Date(value.picker.endDate._d);
    this.form.patchValue({
      startDate: this._datePipe.transform(start, 'yyyy-MM-ddTHH:mm:ssZ'),
      endDate: this._datePipe.transform(end, 'yyyy-MM-ddTHH:mm:ssZ'),
    });
  }

  public onCancelFilterDate() {
    this.form.patchValue({
      startDate: '',
      endDate: '',
    });
  }

  public onOpenNotificationsModal(
    event: Event,
    activityLog: ActivityInterface
  ) {
    event.preventDefault();

    const subscription = this.survey$
      .pipe(
        map(({ subscriber_emails = [] }) => ({
          date: this.time(activityLog.timestamp),
          emails: subscriber_emails,
        })),
        switchMap(({ date, emails }) => this._notificationModal$(date, emails))
      )
      .subscribe();

    this._subscriptions.add(subscription);
  }

  public time(timestamp: number): string {
    const activityDate = new Date(timestamp);
    const dayName = Constants.DAYS[activityDate.getDay()];
    const dateString = `${dayName}, ${activityDate.toLocaleString('default', {
      month: 'long',
    })} ${activityDate.getDate()}`;
    // Ensure single digit minutes have a leading 0
    const stringMinutes =
      (activityDate.getMinutes() < 10 ? '0' : '') + activityDate.getMinutes();
    const timeString = `${activityDate.getHours()}:${stringMinutes}`;
    return `${dateString} @ ${timeString}`;
  }

  public userType(name: string): string {
    const _name = name.toLowerCase();

    if (_name.includes('api')) {
      return 'API Integration';
    }

    if (_name.includes('purespectrum platform')) {
      return 'Auto System';
    }

    return 'User';
  }

  private formFormatter(
    formValue: Partial<FormInterface>
  ): ActivityLogParamsInterface {
    const params: ActivityLogParamsInterface = {
      category: formValue.category?.map(({ value }) => value),
      feature: formValue.feature?.map(({ value }) => value),
      message: formValue.message,
      surveyStatus: formValue.status?.map(({ value }) => value),
      user: formValue.user?.map(({ value }) => value),
      userType: formValue.userType?.map(({ value }) => value),
      startDate: formValue.startDate,
      endDate: formValue.endDate,
    };

    const definedParams = Object.entries(params).filter(
      ([_key, val]) => !!val && val.length > 0
    );
    return { ...Object.fromEntries(definedParams) };
  }

  private _extractUsers(logs: ActivityInterface[] = []) {
    return logs
      .filter(
        ({ username }) =>
          !username.toLowerCase().includes(Constants.USER_TYPES.API) &&
          !username.toLowerCase().includes(Constants.USER_TYPES.PLATFORM)
      )
      .map(({ username, user_email }) => ({
        name: username,
        email: user_email || username,
      }));
  }

  private _notificationModal$(date: string, emails: string[] = []) {
    const modalRef = this._modal.open(NotificationsModalComponent, {
      data: { date, emails },
    });

    return modalRef.onClose$;
  }

  private _emptyTableMessage(
    defaultExpirationTime: number,
    surveyCreationDate: number,
    filtering: boolean
  ): string {
    const isExpired =
      new Date().getTime() >
      Milliseconds.fromDays(defaultExpirationTime).add(surveyCreationDate).time;

    if (isExpired) {
      return Constants.NO_DATA_MESSAGES.expiredActivities.replace(
        '[days]',
        `${defaultExpirationTime}`
      );
    }

    if (filtering) {
      return Constants.NO_DATA_MESSAGES.noFilteredActivities;
    }

    return Constants.NO_DATA_MESSAGES.noActivities;
  }
}
